import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {TiledReductionHelper} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/tiled-reduction-helper-hal"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/mean-and-variance/definition"
import {HalImage} from "@cm/hal"
import {assertNoBatching} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_HIGH} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

// Internal implementation detail of ImageOpNodeMeanAndVariance (mean-and-variance/node.ts): produces a
// single 2x1 image packing [mean, variance] side by side. Not registered as a graph node - the outer node
// constructs it directly and exposes its two halves via separate ImageOpExtractRegionHal views, so the
// public mean/variance outputs share this one batched computation instead of each re-streaming the source.
type Definition = typeof definition

export class ImageOpMeanAndVarianceCombinedHal extends ImageOpHal<Definition, ImageOpMeanAndVarianceCombinedHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpMeanAndVarianceCombinedHalData>): ImageOpMeanAndVarianceCombinedHalData {
        return new ImageOpMeanAndVarianceCombinedHalData({
            ...args,
            descriptor: {width: 2, height: 1, channels: args.parameters.sourceImage.descriptor.channels},
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_HIGH,
        })
    }
}

class ImageOpMeanAndVarianceCombinedHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpMeanAndVarianceCombinedHalData>) {
        super(args)
        // one shared batched pass computes mean and mean-of-squares together, so this never streams the
        // source region twice
        this.tiledReductionHelper = new TiledReductionHelper(this.ctx, [{operator: "mean"}, {operator: "mean-square"}])
    }

    protected override dispose() {
        this.tiledReductionHelper.dispose()
        super.dispose()
    }

    protected override async writeRegion(outputImage: HalImage, _mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {sourceImage} = this.parameters
        sourceImage.addRef("MeanAndVarianceCombined.writeRegion - sourceImage")
        const temps: {
            ref: HalImage
            release(): void
        }[] = []
        const createTemp = (channels: number) => {
            const image = this.ctx.createImagePhysical({width: 1, height: 1, channels})
            temps.push(image)
            return image.ref
        }
        try {
            assertNoBatching(sourceImage.descriptor)
            const channels = sourceImage.descriptor.channels

            const mean = createTemp(channels)
            const meanOfSquares = createTemp(channels)
            await this.tiledReductionHelper.reduceAll({
                sourceImage,
                targets: [mean, meanOfSquares],
                options,
            })

            const meanSquared = createTemp(channels)
            this.ctx.arithmeticHelper.mul({
                operandA: mean,
                operandB: mean,
                result: meanSquared,
            })

            const rawVariance = createTemp(channels)
            this.ctx.arithmeticHelper.sub({
                operandA: meanOfSquares,
                operandB: meanSquared,
                result: rawVariance,
            })

            // clamps a tiny negative value from floating-point cancellation (e.g. near-zero-variance solid-color regions) up to exactly 0
            const variance = createTemp(channels)
            this.ctx.arithmeticHelper.clampMin({
                operand: rawVariance,
                min: 0,
                result: variance,
            })

            this.ctx.blit({
                sourceImage: mean,
                targetImage: outputImage,
                targetOffset: {
                    x: 0,
                    y: 0,
                },
            })
            this.ctx.blit({
                sourceImage: variance,
                targetImage: outputImage,
                targetOffset: {
                    x: 1,
                    y: 0,
                },
            })
        } finally {
            temps.forEach((t) => t.release())
            sourceImage.release("MeanAndVarianceCombined.writeRegion - sourceImage")
        }
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, _sourceRegions: Regions) {
        this.invalidateRegions() // always invalidate the entire result when the source changes
    }

    private readonly tiledReductionHelper: TiledReductionHelper
}
